import { useCallback, useMemo, useState } from "react";

export type FormStateChangeHandler = (key: string) => (value: any) => void;

export const useFormState = <T>({
  initialState,
  validate,
  onSubmit,
  onReset,
}: {
  initialState: T;
  validate?: (state: T) => Partial<Record<keyof T, string>>;
  onSubmit?: (state: T) => void;
  onReset?: () => void;
}) => {
  const [state, setState] = useState<T>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const initialStateMemo = useMemo(() => initialState, [initialState]);

  const handleReset = useCallback(() => {
    setState(initialStateMemo);
    if (onReset) onReset();
  }, [initialStateMemo, onReset]);

  const handleChange = (key: string, key2?: string) => (value: any) => {
    //* If key2 is provided, we are updating a nested object (e.g., meta_box fields)
    if (key2) {
      const validationErrors = validate?.({
        ...state,
        [key2]: { ...state[key2 as keyof T], [key]: value },
      });

      setState((prev) => ({
        ...prev,
        [key2]: { ...prev[key2 as keyof T], [key]: value },
      }));

      if (validate && validationErrors) {
        setErrors(validationErrors);
      }

      return;
    }

    //* If key2 is not provided, we are updating a top-level field
    const validationErrors = validate?.({ ...state, [key]: value });
    setState((prev) => ({ ...prev, [key]: value }));
    if (validate && validationErrors) {
      setErrors(validationErrors);
    }
  };

  const handleSubmit = () => {
    const validationErrors = validate?.(state);
    if (
      validate &&
      validationErrors &&
      Object.values(errors).some((error) => error !== undefined)
    ) {
      setErrors(validationErrors);
      return;
    }
    onSubmit?.(state);
  };

  return {
    state,
    setState,
    errors,
    hasError: Object.values(errors).some((error) => error !== undefined),
    handleChange,
    handleSubmit,
    handleReset,
  };
};
